/**
 * Open orders: review lines, discount, set an exact total, and submit.
 *
 * Discounts and exact totals are gated server-side (issue I-49); this screen
 * only hides the controls a role cannot use and reports the refusal plainly
 * when the server declines.
 */

import { useCallback, useEffect, useState } from 'react';

import * as api from '../lib/api';
import { useSession } from '../lib/session';

/** Discounts above this need `discount.override`; kept in step with the backend. */
const DISCOUNT_THRESHOLD = 20;

export default function OrdersPage() {
  const { can } = useSession();
  const [orders, setOrders] = useState<{ name: string; lines: number; total: number }[]>([]);
  const [active, setActive] = useState<string>('');
  const [detail, setDetail] = useState<{
    items: Record<string, api.OrderLine>;
    total: number;
  } | null>(null);
  const [newOrder, setNewOrder] = useState('');
  const [percent, setPercent] = useState('10');
  const [exactTotal, setExactTotal] = useState('');
  const [payment, setPayment] = useState('cash');
  const [customer, setCustomer] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const loadOrders = useCallback(async () => {
    try {
      const data = await api.listOrders();
      setOrders(data.orders);
      if (!active && data.orders.length) setActive(data.orders[0].name);
    } catch (err) {
      setMessage((err as api.ApiError).message);
    }
  }, [active]);

  const loadDetail = useCallback(async (name: string) => {
    if (!name) {
      setDetail(null);
      return;
    }
    try {
      const data = await api.getOrder(name);
      setDetail({ items: data.items, total: data.total });
      setExactTotal(data.total.toFixed(2));
    } catch (err) {
      setMessage((err as api.ApiError).message);
      setDetail(null);
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    void loadDetail(active);
  }, [active, loadDetail]);

  /** Run an action, surfacing the server's refusal text verbatim. */
  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setMessage('');
    try {
      await action();
      setMessage(success);
      await loadOrders();
      await loadDetail(active);
    } catch (err) {
      setMessage((err as api.ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit(allowOverdraw = false) {
    setBusy(true);
    setMessage('');
    try {
      const result = await api.submitOrder(active, {
        payment_method: payment,
        customer: customer.trim(),
        allow_overdraw: allowOverdraw,
      });
      let summary = `Order submitted. Sale #${result.sale_id}, $${result.total.toFixed(2)}.`;
      if (result.credit_balance !== null) {
        summary += ` Store credit remaining: $${result.credit_balance.toFixed(2)}.`;
      }
      // Problems are per-line and non-fatal; the rest of the order still went
      // through, so the operator needs to see exactly what did not — and that
      // the unsold lines are still sitting on the order rather than lost.
      if (result.problems.length) {
        summary += ` Not sold: ${result.problems.join('; ')}.`;
      }
      if (result.order_kept) {
        summary += ' The unsold lines are still on this order.';
        await loadDetail(active);
      } else {
        setActive('');
      }
      setMessage(summary);
      await loadOrders();
    } catch (err) {
      const apiError = err as api.ApiError;
      // 409 here means the store-credit balance does not cover the order.
      // Nothing has been sold yet, so the operator can decide and retry.
      if (apiError.status === 409 && !allowOverdraw) {
        const proceed = window.confirm(
          `${apiError.message}

Submit anyway and let the balance go negative?`,
        );
        if (proceed) {
          setBusy(false);
          await submit(true);
          return;
        }
      }
      setMessage(apiError.message);
    } finally {
      setBusy(false);
    }
  }

  const lines = Object.entries(detail?.items ?? {});
  const percentValue = Number(percent) || 0;

  return (
    <div className="page">
      <div className="toolbar">
        <select value={active} onChange={(event) => setActive(event.target.value)}>
          <option value="">- select an order -</option>
          {orders.map((order) => (
            <option key={order.name} value={order.name}>
              {order.name} ({order.lines} lines, ${order.total.toFixed(2)})
            </option>
          ))}
        </select>
        <input
          placeholder="New order name"
          value={newOrder}
          onChange={(event) => setNewOrder(event.target.value)}
        />
        <button
          type="button"
          disabled={!newOrder.trim() || busy}
          onClick={() =>
            void run(async () => {
              await api.createOrder(newOrder.trim());
              setActive(newOrder.trim());
              setNewOrder('');
            }, 'Order created.')
          }
        >
          Create order
        </button>
      </div>

      {message && <p className="notice">{message}</p>}

      {!active || !detail ? (
        <p className="muted">Select or create an order.</p>
      ) : (
        <div className="split">
          <div className="table-wrap">
            <table className="items">
              <thead>
                <tr>
                  {/* The item's internal id, not the manufacturer's UPC. */}
                  <th>ID</th>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Line</th>
                  <th>Database</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lines.map(([upc, line]) => (
                  <tr key={upc}>
                    <td className="mono">{upc}</td>
                    <td>{line.name}</td>
                    <td className="num">{line.count}</td>
                    <td className="num">${Number(line.price).toFixed(2)}</td>
                    <td className="num">${(line.count * line.price).toFixed(2)}</td>
                    <td className="muted small">{line.db_path}</td>
                    <td>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() =>
                          void run(
                            () => api.deleteOrderItem(active, upc),
                            'Line removed.',
                          )
                        }
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={7} className="muted">
                      This order is empty. Add items from the Sell tab.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <p className="total">Order total: ${detail.total.toFixed(2)}</p>
          </div>

          <aside className="card">
            <h2>{active}</h2>

            {can('discount.standard') && (
              <div className="field-row">
                <label>
                  Discount %
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={percent}
                    onChange={(event) => setPercent(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => api.discountOrder(active, percentValue),
                      `Applied ${percentValue}% off.`,
                    )
                  }
                >
                  Apply
                </button>
              </div>
            )}
            {percentValue > DISCOUNT_THRESHOLD && !can('discount.override') && (
              <p className="warn small">
                Discounts above {DISCOUNT_THRESHOLD}% need a manager; this will be
                refused.
              </p>
            )}

            {can('checkout.set_total') && (
              <div className="field-row">
                <label>
                  Set total to
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={exactTotal}
                    onChange={(event) => setExactTotal(event.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () => api.setOrderTotal(active, Number(exactTotal) || 0),
                      `Order set to $${Number(exactTotal).toFixed(2)}.`,
                    )
                  }
                >
                  Set
                </button>
              </div>
            )}

            <label>
              Payment
              <select value={payment} onChange={(event) => setPayment(event.target.value)}>
                <option value="cash">Cash</option>
                <option value="trade credit">Trade credit</option>
              </select>
            </label>

            {payment === 'trade credit' && (
              <label>
                Customer
                <input
                  value={customer}
                  onChange={(event) => setCustomer(event.target.value)}
                  placeholder="Store credit account"
                />
              </label>
            )}

            <button
              type="button"
              className="primary"
              disabled={busy || lines.length === 0}
              onClick={() => void submit(false)}
            >
              Submit checkout
            </button>

            <button
              type="button"
              className="ghost danger"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  await api.deleteOrder(active);
                  setActive('');
                }, 'Order deleted.')
              }
            >
              Delete order
            </button>
          </aside>
        </div>
      )}
    </div>
  );
}
